import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card,
  CardContent,
  CardActionArea,
  Alert,
  Chip,
  Collapse,
  Grid
} from '@mui/material';
import useAppNavigation from '../hooks/useAppNavigation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RoomIcon from '@mui/icons-material/Room';
import PlaceIcon from '@mui/icons-material/Place';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../components/Layout';
import LoadingOverlay from '../components/LoadingOverlay';
import { directApi } from '../services/api';

// Import styling from our design system
import { colors } from '../styles/styleConstants';

const Locations = () => {
  const navigate = useAppNavigation();
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Initialize expanded state from sessionStorage to preserve state on back navigation
  const [expandedLocations, setExpandedLocations] = useState(() => {
    try {
      const saved = sessionStorage.getItem('locations_expanded_state');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Fetching locations...');
        
        const response = await directApi.get('/locations');
        const data = response.data;
        
        setLocations(data.locations || []);
        setStats(data.stats || {});
        
        console.log(`Loaded ${data.locations?.length || 0} locations`);
        
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError('Failed to load locations. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLocations();
  }, []);

  // Toggle location expansion and save to sessionStorage
  const toggleLocation = (locationKey) => {
    setExpandedLocations(prev => {
      const newState = {
        ...prev,
        [locationKey]: !prev[locationKey]
      };
      // Save to sessionStorage to preserve state on back navigation
      sessionStorage.setItem('locations_expanded_state', JSON.stringify(newState));
      return newState;
    });
  };

  // Build full location path from location hierarchy
  const buildLocationPath = (location, parentKey = '') => {
    // parentKey format: "-Home-Bedroom-Master"
    // We need to convert it to "Home - Bedroom - Master - CurrentLocation"
    const parts = parentKey.split('-').filter(p => p.length > 0);
    parts.push(location.name);
    return parts.join(' - ');
  };

  // Navigate to CreateBox with prepopulated location
  const handleAddBox = (location, parentKey) => {
    const locationPath = buildLocationPath(location, parentKey);
    // Store origin info so we can return to Locations with tree expanded after box creation
    sessionStorage.setItem('createbox_origin', JSON.stringify({
      from: 'locations',
      locationPath: locationPath,
      expandedState: expandedLocations
    }));
    navigate.to.CREATE_BOX({ state: { prepopulatedLocation: locationPath } });
  };

  // Get icon based on location level
  const getLocationIcon = (level) => {
    switch(level) {
      case 1: return <LocationOnIcon />;
      case 2: return <RoomIcon />;
      case 3: return <PlaceIcon />;
      default: return <LocationOnIcon />;
    }
  };

  // Render location item recursively
  const renderLocationItem = (location, parentKey = '') => {
    const locationKey = `${parentKey}-${location.name}`;
    const hasChildren = location.children && location.children.length > 0;
    const isExpanded = expandedLocations[locationKey] || false;
    
    return (
      <Box key={locationKey} sx={{ mb: 1 }}>
        <Card 
          variant="outlined"
          sx={{ 
            backgroundColor: colors.softWhite,
            borderColor: colors.actionBlue,
            borderWidth: location.level === 1 ? 2 : 1,
            ml: (location.level - 1) * 2
          }}
        >
          <CardActionArea 
            onClick={() => toggleLocation(locationKey)}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Box sx={{ color: colors.actionBlue, mr: 2 }}>
                    {getLocationIcon(location.level)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant={location.level === 1 ? "h6" : "body1"} 
                      sx={{ 
                        fontWeight: location.level === 1 ? 600 : 500,
                        color: colors.slateBlue,
                        mb: 0.5
                      }}
                    >
                      {location.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        icon={<InventoryIcon />}
                        label={`${location.box_count} ${location.box_count === 1 ? 'box' : 'boxes'}`}
                        size="small"
                        sx={{ 
                          backgroundColor: colors.lightGray,
                          color: colors.slateBlue,
                          height: 24
                        }}
                      />
                      <Chip 
                        icon={<CategoryIcon />}
                        label={`${location.item_count} ${location.item_count === 1 ? 'item' : 'items'}`}
                        size="small"
                        sx={{ 
                          backgroundColor: colors.lightGray,
                          color: colors.slateBlue,
                          height: 24
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                
                {(hasChildren || location.box_count > 0) && (
                  <Box sx={{ color: colors.actionBlue, display: 'flex', alignItems: 'center' }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                )}
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
        
        {/* Show children and/or direct boxes when expanded */}
        <Collapse in={isExpanded}>
          {/* Action buttons row */}
          <Box sx={{ mt: 1, ml: (location.level - 1) * 2 + 2, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Add Box button - always visible when expanded */}
            <Chip
              icon={<AddIcon />}
              label={`Add box to ${location.name}`}
              onClick={() => handleAddBox(location, parentKey)}
              sx={{
                backgroundColor: colors.successGreen || '#4caf50',
                color: colors.softWhite,
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#388e3c'
                }
              }}
            />
            
            {/* List of clickable box names - only if there are direct boxes */}
            {(location.direct_boxes?.length > 0 || (!hasChildren && location.boxes?.length > 0)) && (
              <>
                {(location.direct_boxes || location.boxes || []).map((box) => (
                  <Chip
                    key={box.id}
                    icon={<InventoryIcon />}
                    label={box.name}
                    onClick={() => navigate.to.BOX_DETAIL(box.id)}
                    sx={{
                      backgroundColor: colors.actionBlue,
                      color: colors.softWhite,
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: colors.slateBlue
                      }
                    }}
                  />
                ))}
              </>
            )}
          </Box>
          
          {/* Show children if has children */}
          {hasChildren && (
            <Box sx={{ mt: 1 }}>
              {location.children.map(child => renderLocationItem(child, locationKey))}
            </Box>
          )}
        </Collapse>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Layout title="Explorer">
        <LoadingOverlay message="Loading explorer..." />
      </Layout>
    );
  }

  return (
    <Layout title="Explorer">
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Header */}
      {stats && (
        <Box 
          sx={{ 
            p: 3, 
            mb: 3, 
            backgroundColor: colors.slateBlue,
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: colors.softWhite }}>
            Location Overview
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                padding: 1.5,
                textAlign: 'center'
              }}>
                <LocationOnIcon sx={{ fontSize: 28, mb: 0.5, color: colors.softWhite }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.softWhite }}>
                  {stats.total_locations}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.7)' }}>
                  Locations
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                padding: 1.5,
                textAlign: 'center'
              }}>
                <InventoryIcon sx={{ fontSize: 28, mb: 0.5, color: colors.softWhite }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.softWhite }}>
                  {stats.total_boxes_with_location}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.7)' }}>
                  Boxes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                padding: 1.5,
                textAlign: 'center'
              }}>
                <CategoryIcon sx={{ fontSize: 28, mb: 0.5, color: colors.softWhite }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.softWhite }}>
                  {stats.total_items}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.7)' }}>
                  Items
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Locations List */}
      {locations.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center', backgroundColor: colors.softWhite }}>
          <LocationOnIcon sx={{ fontSize: 64, color: colors.mediumSlateBlue, mb: 2 }} />
          <Typography variant="h6" sx={{ color: colors.slateBlue, mb: 1 }}>
            No Locations Yet
          </Typography>
          <Typography variant="body2" sx={{ color: colors.mediumSlateBlue }}>
            Add locations to your boxes to organize them by room or area.
          </Typography>
        </Card>
      ) : (
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: colors.slateBlue,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <LocationOnIcon />
            All Locations
          </Typography>
          
          {locations.map(location => renderLocationItem(location))}
        </Box>
      )}
    </Layout>
  );
};

export default Locations;
